import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Switch,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  RefObject,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IParcelsList } from 'components/generics/Interface/Api/Response/Exploitation/Parcels/IParcelsList';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import MapIcon from '@mui/icons-material/Map';
import { customButtonTheme, customRedButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import DoneIcon from '@mui/icons-material/Done';
import { ExploitationContext, LoadingContext } from 'utils/context';
import { ILoadingContext } from 'components/generics/Interface/Context/ILoadingContext';
import { toast } from 'react-toastify';
import { displayErrorMessage, sortWithLocale } from 'utils/tools_functions';
import './procedureImport.scss';
import { IParcelImportedData } from 'components/generics/Interface/Api/Response/Exploitation/Parcels/IParcelImportedData';
import { ICampaign } from 'components/generics/Interface/Exploitation/Parcels/ICampaign';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKey,
  getCultureCampaignName,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import MapImport from './MapImport';
import React from 'react';
import { importMatchingParcelsResult } from 'services/API/Cooperative';
import { ScrollToTarget } from 'components/generics/ScrollToTarget';
import FilterComponent from './ProcedureImportFilterComponent';
import OptimizedCard from './ProcedureImportCardComponent';
import { checkBoxTheme } from 'assets/styles/themes/Checkbox_theme';
import { ImportContext, useCheckStatus } from 'providers/ImportStatus';
import { useTranslation } from 'react-i18next';
import { COLORS, IMPORT_FROM } from 'utils/GlobalVariables';

export interface IParcelImported extends IParcelsList {
  importedParcel: IParcelImportedData;
  probability: number;
  hasCorrespondence: boolean;
  matchedParcel: IParcelsList | null | undefined;
}

export type AutocompleteOption = {
  label: string;
  value: number | null;
};

type ProcedureCampaignParams = {
  exploitationSelected: any;
  loadDataExploitation: (isOrganic: boolean, exploitationId: number, targetCampaign?: string) => {};
  forecastCampaign: ICampaign;
};

const MapImportTyped = MapImport as React.ComponentType<{
  forecastCampaign: ICampaign;
  parcelsList: IParcelImported[] | IParcelsList[];
  name: string;
  parcelHover: string | number | null;
  mapContainer: RefObject<null>;
  setHighlightedCard: React.Dispatch<React.SetStateAction<string | null>>;
  importedParcels: IParcelImported[];
  setParcelMapHovered: React.Dispatch<React.SetStateAction<number | string>>;
  parcelMapHovered: number | string | null;
  parcelFromCardToZoom: {
    assoliaParcel: number | string;
    importedParcel: number | string;
    coordinates: any[];
  };
  parcelAssoliaToZoom: { assoliaParcel: number | string; timestamp: number; coordinates: any[] };
  visibilityCard: Map<string, boolean>;
}>;

const ProcedureImport = ({
  exploitationSelected,
  loadDataExploitation,
  forecastCampaign,
}: ProcedureCampaignParams) => {
  const { t, i18n } = useTranslation();
  const HeaderRef = useRef(null);
  const assoliaMapContainer = useRef(null);
  const importedMapContainer = useRef(null);
  const { exploitationContext } = useContext(ExploitationContext);
  const { setCustomText, setLoading } = useContext(LoadingContext) as ILoadingContext;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    result: { data } = {},
    importType,
    nextCampaignChosen,
    jobId,
    redirectTo,
    from,
  } = location.state || {};
  const exploitationId = data[0]?.exploitationId;
  const exploitationName = data[0]?.exploitationName;
  const importedParcelsList = data[0]?.parcels;
  const referenceCampaign = from === 'procedure-import' ? nextCampaignChosen : forecastCampaign;

  // states of the 3 parcels lists
  const [importedParcels, setImportedParcels] = useState<IParcelImported[]>(
    sortWithLocale(importedParcelsList, 'importedParcel.name'),
  );
  const [assoliaParcels, setAssoliaParcels] = useState<IParcelsList[]>(
    filterParcelFromActivityDate((exploitationContext as any)?.parcels, referenceCampaign),
  );

  const assoliaParcelsWithoutCorrespondingImportParcel = useMemo(() => {
    const parcels =
      filterParcelFromActivityDate(
        assoliaParcels?.filter(
          (assoliaParcel: IParcelsList) =>
            !importedParcels?.some(
              (importedParcel: IParcelImported) =>
                importedParcel.matchedParcel?.id === assoliaParcel.id,
            ),
        ),
        referenceCampaign,
      ) || [];

    return sortWithLocale(parcels, 'name');
  }, [assoliaParcels, importedParcels]);
  // states related to the map
  const [isMapImportVisible, setIsMapImportVisible] = useState<boolean>(true);
  const [isMapAssoliaVisible, setIsMapAssoliaVisible] = useState<boolean>(true);
  const coordsAssoliaExist = assoliaParcels?.some((parcel) => parcel.coordinates?.length);
  const coordsImportExist = importedParcels?.some(
    (parcel) => parcel.importedParcel.coordinates?.length,
  );
  const { oneMapDisplayed, bothMapsDisplayed, gridSizes, mainContentWidth } = useMemo(() => {
    const oneMapDisplayed =
      (isMapAssoliaVisible && coordsAssoliaExist) || (isMapImportVisible && coordsImportExist);

    const bothMapsDisplayed =
      isMapAssoliaVisible && isMapImportVisible && coordsAssoliaExist && coordsImportExist;

    const gridSizes = bothMapsDisplayed
      ? { xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }
      : oneMapDisplayed
        ? { xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }
        : { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };

    const mainContentWidth = bothMapsDisplayed ? '33%' : oneMapDisplayed ? '50%' : '100%';

    return { oneMapDisplayed, bothMapsDisplayed, gridSizes, mainContentWidth };
  }, [isMapAssoliaVisible, isMapImportVisible, coordsAssoliaExist, coordsImportExist]);

  // states related to the modal dialogs
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState<boolean>(false);

  // states related to the fiter component
  const [areAssociatedImportedParcelsHiden, setAreAssociatedImportedParcelsHiden] =
    useState<boolean>(false);
  const [importedParcelFilterValue, setImportedParcelFilterValue] = useState<string | null>(null);
  const visibilityCard = useMemo(() => {
    const map = new Map<string, boolean>();

    importedParcels.forEach((parcel) => {
      let isVisible = true;

      if (areAssociatedImportedParcelsHiden && parcel.matchedParcel !== null) {
        isVisible = false;
      }

      if (
        importedParcelFilterValue &&
        !parcel.importedParcel.name.toLowerCase().includes(importedParcelFilterValue)
      ) {
        isVisible = false;
      }

      map.set(parcel.importedParcel.uuid, isVisible);
    });

    return map;
  }, [importedParcelFilterValue, importedParcels, areAssociatedImportedParcelsHiden]);
  const filteredImportedParcelsCount = useMemo(() => {
    return (
      importedParcels.length -
      importedParcels.filter((parcel) => parcel.matchedParcel === null).length
    );
  }, [importedParcels]);

  // states to control the accordions of the cards
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    if (bothMapsDisplayed) {
      return new Set();
    }
    return new Set(importedParcels.map((parcel) => parcel.importedParcel.uuid));
  });
  const expandAll = useMemo(() => {
    if (importedParcels.length === 0) return false;
    return importedParcels.every((parcel) => expandedIds.has(parcel.importedParcel.uuid));
  }, [expandedIds, importedParcels]);

  // Function to handle global switch
  const handleExpandAllToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isExpanded = event.target.checked;

    if (isExpanded) {
      setExpandedIds(new Set(importedParcels.map((parcel) => parcel.importedParcel.uuid)));
    } else {
      setExpandedIds(new Set());
    }
  };

  // Function to handle individual accordion switch
  const handleAccordionChange = useCallback(
    (uuid: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedIds((prev) => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(uuid);
        } else {
          newSet.delete(uuid);
        }
        return newSet;
      });
    },
    [],
  );

  // base options = unmatched assolia parcels
  const baseAutocompleteOptions = useMemo(() => {
    return assoliaParcelsWithoutCorrespondingImportParcel?.map((assoliaParcel) => ({
      label: `${assoliaParcel.name} (${assoliaParcel.surface} ha)`,
      value: assoliaParcel.id,
    }));
  }, [assoliaParcelsWithoutCorrespondingImportParcel]);

  const importedParcelFilterOptions = useMemo(() => {
    return Array.from(new Set(importedParcels.map((parcel) => parcel.importedParcel.name))).sort();
  }, [importedParcels]);
  const [parcelAssoliaHover, setParcelAssoliaHover] = useState<number | string | null>('none');
  const [parcelImportHover, setParcelImportHover] = useState<number | string | null>('none');

  useEffect(() => {
    if (!exploitationSelected) {
      setCustomText(t('message.loadingData'));
      setLoading(true);
      if (from === 'procedure-import') {
        loadDataExploitation(false, exploitationId, referenceCampaign?.stringFormat);
      } else {
        loadDataExploitation(false, exploitationId);
      }
    } else {
      setAssoliaParcels(
        filterParcelFromActivityDate((exploitationContext as any)?.parcels, referenceCampaign),
      );
      const coordsAssoliaExist = (exploitationContext as any)?.parcels?.some(
        (parcel: IParcelsList) => parcel.coordinates?.length,
      );
      setIsMapAssoliaVisible(coordsAssoliaExist);
      const coordsImportExist = importedParcelsList?.some(
        (parcel: IParcelImported) => parcel.importedParcel.coordinates?.length,
      );
      setIsMapImportVisible(coordsImportExist);
      const bothMapsDisplayed =
        isMapAssoliaVisible && isMapImportVisible && coordsAssoliaExist && coordsImportExist;
      if (bothMapsDisplayed) {
        setExpandedIds(new Set());
      } else {
        setExpandedIds(new Set(importedParcels.map((parcel) => parcel.importedParcel.uuid)));
      }
    }
  }, [exploitationSelected]);

  const handleChange = useCallback(
    (importedParcelId: string) =>
      (event: SyntheticEvent<Element, Event>, value: any, reason: any, details?: any) => {
        if (reason === 'clear') {
          const updatedImportedParcels = importedParcels.map((parcel) => {
            if (parcel.importedParcel.uuid === importedParcelId) {
              setChosenNames((prev) => {
                const newMap = new Map(prev);
                newMap.set(importedParcelId, parcel.importedParcel.name);
                return newMap;
              });
              return {
                ...parcel,
                matchedParcel: null,
              };
            } else {
              return parcel;
            }
          });
          setImportedParcels(updatedImportedParcels);
          return;
        }

        if (!value) return;

        let hasChange = false;
        const updatedImportedParcels = importedParcels.map((parcel, index) => {
          if (
            parcel.importedParcel.uuid === importedParcelId &&
            parcel.matchedParcel?.id !== value?.value &&
            value?.value !== null
          ) {
            hasChange = true;
            const newMatchedParcel = assoliaParcelsWithoutCorrespondingImportParcel.find(
              (assoliaParcel: IParcelsList) => assoliaParcel.id === value?.value,
            );

            setChosenNames((prev) => {
              const newMap = new Map(prev);
              newMap.set(importedParcelId, newMatchedParcel?.name || parcel.importedParcel.name);
              return newMap;
            });

            return {
              ...parcel,
              matchedParcel: newMatchedParcel,
            };
          }
          if (
            parcel.importedParcel.uuid !== importedParcelId &&
            parcel.matchedParcel?.id === value?.value
          ) {
            return {
              ...parcel,
              matchedParcel: null,
            };
          }
          return parcel;
        });
        setImportedParcels(updatedImportedParcels);
      },
    [importedParcels, assoliaParcels, assoliaParcelsWithoutCorrespondingImportParcel],
  );

  const RenderCancelImportProcedure = () => {
    const { resetImportContext } = useContext(ImportContext);
    const { setCustomText } = useContext(LoadingContext) as ILoadingContext;

    const handleCancelImport = async () => {
      try {
        setCustomText(t('message.cancellingImport', { importType }));
        toast.warning(t('dialog.importCancelled', { importType }));
        resetImportContext();
        if (from === IMPORT_FROM.classic) {
          navigate(`/${redirectTo}/import`);
        } else if (from === IMPORT_FROM.procedure) {
          navigate(`/exploitation/campaign`, {
            state: {
              nextCampaignChosen: nextCampaignChosen,
            },
          });
        }
      } catch (error: any) {
        toast.error(error?.response?.data.message ?? displayErrorMessage('ERR_UNEXPECTED'));
      }
    };

    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openCancelDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <h3>{t('dialog.importCancellationTitle')}</h3>
            <p>{t('dialog.importCancellationMessage')}</p>
            <p>{t('dialog.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelImport}>
              {t('button.confirmCancellation')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenCancelDialog(false)}>
              {t('dialog.stayOnPage')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const RenderFinalizeImportProcedure = () => {
    const { setCustomText } = useContext(LoadingContext) as ILoadingContext;
    const checkStatus = useCheckStatus();
    const importedParcelWithoutCorrespondingAssoliaParcel =
      importedParcels.filter((importedParcel) => importedParcel.matchedParcel === null) || [];
    const hasImportedParcelWithoutCorrespondingAssoliaParcel =
      importedParcelWithoutCorrespondingAssoliaParcel.length > 0;

    const handleFinalizeImport = async () => {
      setOpenFinalizeDialog(false);
      try {
        setCustomText(t('message.finalizingImport', { importType }));
        const cleanedImportedParcels = importedParcels.map(
          (parcel: IParcelImported, index: number) => {
            return {
              importedParcel: { uuid: parcel.importedParcel.uuid },
              matchedParcel: { id: parcel.matchedParcel?.id ?? null },
              name: chosenNames.get(parcel.importedParcel.uuid),
            };
          },
        );
        const finalData = {
          matchedParcels: [
            {
              exploitationId: exploitationId,
              exploitationName: exploitationName,
              parcels: cleanedImportedParcels,
            },
          ],
        };
        const response = await importMatchingParcelsResult(i18n.language, jobId, finalData);
        checkStatus(response.data.data.jobId);
      } catch (error: any) {
        toast.error(error?.response?.data.message ?? displayErrorMessage('ERR_UNEXPECTED'));
      }
    };

    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openFinalizeDialog}>
          {hasImportedParcelWithoutCorrespondingAssoliaParcel && (
            <DialogTitle>{t('common.warning')}</DialogTitle>
          )}
          <DialogContent dividers>
            <h3>
              {from === IMPORT_FROM.procedure
                ? t('title.importAndProcedureFinalization')
                : t('title.importFinalization')}
            </h3>
            {hasImportedParcelWithoutCorrespondingAssoliaParcel && (
              <React.Fragment>
                <p>
                  {t('message.youHaveParcelsCount', {
                    count: importedParcelWithoutCorrespondingAssoliaParcel.length,
                  })}{' '}
                  {t('message.importedParcelsNoMatch')}
                </p>
                <p>{t('message.importWillCreateParcels')}</p>
              </React.Fragment>
            )}
            {assoliaParcelsWithoutCorrespondingImportParcel.length > 0 && (
              <React.Fragment>
                <p>
                  {t('message.youHaveParcelsCount', {
                    count: assoliaParcelsWithoutCorrespondingImportParcel.length,
                  })}{' '}
                  {t('message.exploitationParcelsNoMatch')}
                </p>
                <p>{t('message.parcelsWillBeDeleted')}</p>
              </React.Fragment>
            )}
            <p>{t('dialog.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFinalizeImport}>
              {t('common.validate')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenFinalizeDialog(false)}>
              {t('dialog.stayOnPage')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  // Chosen Names handler
  const [chosenNames, setChosenNames] = useState<Map<string, string>>(() => {
    return new Map(
      importedParcelsList?.map((parcel: IParcelImported) => [
        parcel.importedParcel.uuid,
        parcel.matchedParcel?.name ?? parcel.importedParcel.name,
      ]) || [],
    );
  });

  const handleChangeParcelName = useCallback((event: SelectChangeEvent, uuid: string) => {
    const newName = event.target.value;
    setChosenNames((prev) => {
      const newMap = new Map(prev);
      newMap.set(uuid, newName);
      return newMap;
    });
  }, []);

  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);
  const [isMapLegendVisible, setIsMapLegendVisible] = useState<boolean>(false);
  const [parcelMapHovered, setParcelMapHovered] = useState<number | string>('none');
  const [parcelFromCardToZoom, setParcelFromCardToZoom] = useState<{
    assoliaParcel: number | string;
    importedParcel: number | string;
    coordinates: any[];
    timestamp: number;
  }>({
    assoliaParcel: 'none',
    importedParcel: 'none',
    coordinates: [],
    timestamp: 0,
  });
  const [parcelAssoliaToZoom, setParcelAssoliaToZoom] = useState<{
    assoliaParcel: number | string;
    coordinates: any[];
    timestamp: number;
  }>({
    assoliaParcel: 'none',
    coordinates: [],
    timestamp: 0,
  });

  return (
    <Box className="section procedure_import">
      {openCancelDialog && <RenderCancelImportProcedure />}
      {openFinalizeDialog && <RenderFinalizeImportProcedure />}
      <Box className="procedure_import_header">
        <h2 className="title_section title_section_culture">
          {t('title.preFinalizationImportSettings', { importType })}
        </h2>
        <Box className="procedure_import_header_button">
          <ThemeProvider theme={customRedButtonTheme}>
            <Button onClick={() => setOpenCancelDialog(true)}>{t('button.cancelImport')}</Button>
          </ThemeProvider>
          <ThemeProvider theme={customButtonTheme}>
            <Button onClick={() => setOpenFinalizeDialog(true)}>
              {from === IMPORT_FROM.classic
                ? t('button.finalizeImport')
                : t('button.finalizeImportAndProcedure')}
            </Button>
          </ThemeProvider>
        </Box>
      </Box>
      <Divider />
      <h4 className="unmatched_assolia_parcels_title">
        {t('title.unmatchedAssoliaParcels')} (
        {assoliaParcelsWithoutCorrespondingImportParcel
          ?.reduce((acc, parcel) => acc + parcel.surface, 0)
          .toFixed(2)}{' '}
        ha)
      </h4>
      <Box className="unmatched_assolia_parcels">
        <ExpandableDiv />
        <Box className="unmatched_assolia_parcels_list">
          {assoliaParcelsWithoutCorrespondingImportParcel?.map((parcel) => (
            <Tooltip
              arrow
              slotProps={{
                popper: {
                  sx: { inset: '5px auto auto 5px !important' },
                },
              }}
              title={
                <Box className="custom_tooltip">
                  <span>
                    {t('common.name')} : {parcel.name}
                  </span>
                  <span>
                    {t('common.surfaceHa')} : {parcel.surface}
                  </span>
                  <span>
                    {getCultureCampaignKey(1, referenceCampaign)} :{' '}
                    {t(getCultureCampaignName(parcel.parcelCultures, 1, referenceCampaign))}
                  </span>
                  <span>
                    {getCultureCampaignKey(2, referenceCampaign)} :{' '}
                    {t(getCultureCampaignName(parcel.parcelCultures, 2, referenceCampaign))}
                  </span>
                  <span>
                    {t('label.groundType')} : {parcel.groundType.name}
                  </span>
                  <span>
                    {t('common.irrigability')} :{' '}
                    {parcel.isIrrigable ? t('common.yes') : t('common.no')}
                  </span>
                  <span>
                    {t('label.organicType')} : {t(`organicState.${parcel.organicState}`)}
                  </span>
                </Box>
              }
              key={parcel.id}
            >
              <Chip
                label={`${
                  parcel.name.length > 30 ? parcel.name.substring(0, 30) + '...' : parcel.name
                } (${parcel.surface} ha)`}
                className="unmatched_assolia_parcels_chip"
                icon={<InfoIcon sx={{ fontSize: 24 }} />}
                sx={{
                  cursor:
                    parcel?.coordinates && parcel?.coordinates?.length > 0 ? 'pointer' : 'default',
                }}
                onClick={
                  parcel?.coordinates && parcel?.coordinates?.length > 0
                    ? () => {
                        setParcelAssoliaToZoom({
                          assoliaParcel: parcel?.id || 'none',
                          coordinates: parcel.coordinates,
                          timestamp: Date.now(),
                        });
                      }
                    : undefined
                }
              />
            </Tooltip>
          ))}
          {assoliaParcelsWithoutCorrespondingImportParcel?.length === 0 && (
            <p>{t('message.allParcelsAssociated')}</p>
          )}
        </Box>
      </Box>
      <Divider />
      <Box className="main_content_header" ref={HeaderRef}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', xl: 'row' },
            gridColumn: '1',
            gap: '8px',
          }}
        >
          <Stack className="hook-toggle-switch" direction="row" spacing={1} sx={{ height: 20 }}>
            <InputLabel className="hook-toggle-switch__label">{t('common.collapseAll')}</InputLabel>
            <Switch
              checked={expandAll}
              onChange={handleExpandAllToggle}
              slotProps={{
                input: { 'aria-label': 'ant design' },
              }}
              className={`hook-toggle-switch__switch ${
                !expandAll ? 'hook-toggle-switch__switch-disabled' : ''
              }`}
            />
            <InputLabel className="hook-toggle-switch__label">{t('common.expandAll')}</InputLabel>
          </Stack>
          <Divider
            sx={{
              display: { xs: 'none', xl: 'block' },
            }}
            orientation="vertical"
            flexItem
          />
          {(coordsAssoliaExist || coordsImportExist) && (
            <ThemeProvider theme={checkBoxTheme}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: 20,
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: COLORS.green,
                    fontFamily: 'Rubik-Medium',
                    alignSelf: 'center',
                    marginRight: '10px',
                  }}
                >
                  {t('common.map')}
                </FormLabel>
                {coordsAssoliaExist && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isMapAssoliaVisible}
                        onChange={() => setIsMapAssoliaVisible(!isMapAssoliaVisible)}
                      />
                    }
                    label={coordsImportExist ? 'Assolia' : ''}
                  />
                )}
                {coordsImportExist && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isMapImportVisible}
                        onClick={() => setIsMapImportVisible(!isMapImportVisible)}
                      />
                    }
                    label={coordsAssoliaExist ? t('label.imported') : ''}
                  />
                )}
                {oneMapDisplayed && (
                  <Tooltip
                    arrow
                    slotProps={{
                      popper: {
                        sx: { inset: '5px auto auto -2px !important' },
                      },
                    }}
                    title={
                      <Box className="custom_tooltip">
                        {t(
                          isMapLegendVisible ? 'title.hideParcelLegend' : 'title.showParcelLegend',
                        )}
                      </Box>
                    }
                  >
                    <IconButton
                      onClick={() => setIsMapLegendVisible(!isMapLegendVisible)}
                      sx={{
                        paddingLeft: 0,
                        '&:hover': {
                          backgroundColor: COLORS.yellow,
                        },
                        color: COLORS.green,
                      }}
                    >
                      <MapIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </ThemeProvider>
          )}
        </Box>

        <h4 className="imported_parcels_title">
          {t('title.importedParcelsVia', { importType })} (
          {importedParcels
            ?.reduce((acc, parcel) => acc + parcel.importedParcel.surface, 0)
            .toFixed(2)}{' '}
          ha)
        </h4>
        <FilterComponent
          importedParcelFilterValue={importedParcelFilterValue}
          importedParcelFilterOptions={importedParcelFilterOptions}
          setImportedParcelFilterValue={setImportedParcelFilterValue}
          setAreAssociatedImportedParcelsHiden={setAreAssociatedImportedParcelsHiden}
          areAssociatedImportedParcelsHiden={areAssociatedImportedParcelsHiden}
          filteredImportedParcelsCount={filteredImportedParcelsCount}
        />
      </Box>
      {isMapLegendVisible && oneMapDisplayed && (
        <div className="legend_map_container" style={{ margin: '0 80px' }}>
          <div className="legend_title">{t('label.mapParcelLegend')}</div>
          <div className="legend_map_row">
            <div className="outline_legend">
              <div className="stroke conversion"></div>
              <p>{t('simulation.result.common.bioConversion')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke organic"></div>
              <p>{t('simulation.common.bioCulture')}</p>
            </div>
            <div className="outline_legend">
              <div className="stroke conventional"></div>
              <p>{t('label.conventionalCulture')}</p>
            </div>
          </div>
        </div>
      )}
      <Box className="d-flex main_content">
        {coordsAssoliaExist && (
          <Box
            ref={assoliaMapContainer}
            className="map_section"
            sx={{
              width: isMapAssoliaVisible ? mainContentWidth : '0%',
              top: '125px',
            }}
          >
            {isMapAssoliaVisible && (
              <React.Fragment>
                <MapImportTyped
                  forecastCampaign={referenceCampaign}
                  parcelsList={assoliaParcels}
                  name="map-assolia"
                  parcelHover={parcelAssoliaHover}
                  mapContainer={assoliaMapContainer}
                  setHighlightedCard={setHighlightedCard}
                  importedParcels={importedParcels}
                  setParcelMapHovered={setParcelMapHovered}
                  parcelMapHovered={parcelMapHovered}
                  parcelFromCardToZoom={parcelFromCardToZoom}
                  parcelAssoliaToZoom={parcelAssoliaToZoom}
                  visibilityCard={visibilityCard}
                />
              </React.Fragment>
            )}
          </Box>
        )}
        <Box
          className="imported_parcels_card_list"
          sx={{
            width: mainContentWidth,
            marginTop: oneMapDisplayed ? '40px !important' : '10px !important',
            marginLeft: isMapAssoliaVisible ? '0px !important' : '10px !important',
            marginRight: isMapImportVisible ? '0px !important' : '10px !important',
          }}
        >
          <Grid
            container
            spacing={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }}
            className="imported_parcels_cards"
          >
            {importedParcels.map((parcel) => (
              <Grid
                key={parcel.importedParcel.uuid}
                size={gridSizes}
                className="imported_parcels_card_wrapper"
                sx={{
                  display: visibilityCard.get(parcel.importedParcel.uuid) ? 'block' : 'none',
                }}
                title={
                  !expandedIds.has(parcel.importedParcel.uuid) ? parcel.importedParcel.name : ''
                }
              >
                {baseAutocompleteOptions && (
                  <OptimizedCard
                    parcel={parcel}
                    baseAutocompleteOptions={baseAutocompleteOptions}
                    isExpanded={expandedIds.has(parcel.importedParcel.uuid)}
                    forecastCampaign={referenceCampaign}
                    handleAccordionChange={handleAccordionChange}
                    handleChange={handleChange}
                    handleChangeParcelName={handleChangeParcelName}
                    setParcelAssoliaHover={setParcelAssoliaHover}
                    setParcelImportHover={setParcelImportHover}
                    chosenNames={chosenNames}
                    highlightedCard={highlightedCard}
                    setParcelFromCardToZoom={setParcelFromCardToZoom}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
        {coordsImportExist && (
          <Box
            ref={importedMapContainer}
            className="map_section"
            sx={{
              width: isMapImportVisible ? mainContentWidth : '0%',
              top: '125px',
            }}
          >
            {isMapImportVisible && (
              <React.Fragment>
                <MapImportTyped
                  forecastCampaign={referenceCampaign}
                  parcelsList={importedParcels}
                  name="map-import"
                  parcelHover={parcelImportHover}
                  mapContainer={importedMapContainer}
                  setHighlightedCard={setHighlightedCard}
                  importedParcels={importedParcels}
                  setParcelMapHovered={setParcelMapHovered}
                  parcelMapHovered={parcelMapHovered}
                  parcelFromCardToZoom={parcelFromCardToZoom}
                  parcelAssoliaToZoom={parcelAssoliaToZoom}
                  visibilityCard={visibilityCard}
                />
              </React.Fragment>
            )}
          </Box>
        )}
      </Box>
      <ScrollToTarget targetRef={HeaderRef} />
    </Box>
  );
};

export default ProcedureImport;

const ExpandableDiv = () => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box className="expandable_tooltip_container">
      <Tooltip
        arrow
        className="expandable_tooltip"
        title={isExpanded ? null : t('common.moreInfo')}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '16px',
            },
          },
        }}
      >
        <Box
          className="expandable_tooltip_wrapper"
          sx={{
            width: isExpanded ? '300px' : '48px',
            height: isExpanded ? '300px' : '48px',
            '&:hover': {
              '& .MuiSvgIcon-root': {
                color: isExpanded ? '' : 'white',
              },
              backgroundColor: isExpanded ? 'white !important' : COLORS.yellow + ' !important',
              border: isExpanded
                ? '2px solid' + COLORS.green + ' !important'
                : '2px solid' + COLORS.yellow + ' !important',
            },
          }}
          onClick={handleToggle}
        >
          <Box className="expandable_tooltip_header">
            <InfoIcon
              sx={{
                fontSize: '24px',
                color: COLORS.green,
              }}
            />
            <Typography variant="h6" className="expandable_tooltip_title">
              {t('common.informations')}
            </Typography>
          </Box>

          <Box
            sx={{
              opacity: isExpanded ? 1 : 0,
            }}
            className="expandable_tooltip_content"
          >
            <Typography variant="body2" className="expandable_tooltip_paragraph1">
              {t('message.unmatchedExploitationParcels')}
              {t('message.unmatchedExploitationParcelsContinuation')}
            </Typography>

            <Typography variant="body2" className="expandable_tooltip_paragraph2">
              {t('message.manualAssociationHint')}
            </Typography>

            <Typography variant="body2" className="expandable_tooltip_paragraph3">
              {t('message.hoverForParcelDetails')} {t('message.clickToZoomOnMap')}
            </Typography>
          </Box>

          <Box
            sx={{
              opacity: isExpanded ? 1 : 0,
            }}
            className="expandable_tooltip_footer"
          >
            <Typography variant="caption" className="expandable_tooltip_footer_content">
              {t('common.clickToCollapse')}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};
